/**
 * A confirmation that stays disabled until an exact phrase is typed.
 *
 * Used for the two steps on Controls that are hard to walk back: letting the
 * agent send on its own, and rejecting the whole queue. The friction goes on
 * the step that cannot be undone, per the BulkPurge.tsx principle.
 *
 * A declarative <Modal> with the typed text in React state. Two things it
 * replaces: the static Modal.confirm, which does not mount under this app's
 * React 19 setup, and an earlier version that enabled the OK button by querying
 * the DOM — which worked, but no test could drive it.
 */
import { useEffect, useState } from 'react'
import { Input, Modal } from 'antd'

export interface TypedConfirmProps {
  open: boolean
  title: string
  body: React.ReactNode
  phrase: string
  okText: string
  onConfirm: () => Promise<void>
  onClose: () => void
}

export function TypedConfirm({ open, title, body, phrase, okText, onConfirm, onClose }: TypedConfirmProps) {
  const [typed, setTyped] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // A fresh start every time it opens, so a phrase typed for one action can
  // never carry over and pre-arm the next.
  useEffect(() => { if (open) { setTyped(''); setError(null) } }, [open])

  const matches = typed.trim() === phrase

  return (
    <Modal
      open={open}
      title={title}
      width={560}
      okText={okText}
      okButtonProps={{ danger: true, disabled: !matches, 'data-testid': 'cm-typed-confirm-ok' } as never}
      confirmLoading={busy}
      onCancel={onClose}
      onOk={async () => {
        if (!matches) return
        setBusy(true)
        setError(null)
        try {
          await onConfirm()
          onClose()
        } catch (e) {
          // Stay open and say so. Closing on failure would leave the owner
          // believing the agent was switched — or the queue cleared — when it
          // was not.
          setError(e instanceof Error ? e.message : String(e))
        } finally {
          setBusy(false)
        }
      }}
      destroyOnHidden
    >
      <div>{body}</div>
      <p style={{ marginBottom: 6 }}>
        Type <strong>{phrase}</strong> to confirm.
      </p>
      <Input
        data-testid="cm-typed-confirm-input"
        value={typed}
        autoFocus
        onChange={(e) => setTyped(e.target.value)}
        onPressEnter={() => { /* Enter must not bypass the phrase; OK does the work */ }}
      />
      {error && (
        <p data-testid="cm-typed-confirm-error" style={{ color: '#b9453b', marginBottom: 0, marginTop: 8 }}>
          That did not go through: {error}
        </p>
      )}
    </Modal>
  )
}
